import React, { ReactNode } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

type NestedScrollableTabBarProps = {
    children: ReactNode;
};

/**
 * Wrapper component to enable horizontal scrolling of tab bars inside vertical scrollable containers on Android.
 *
 * The issue: react-native-tab-view uses Animated.FlatList from react-native, but when inside a BottomSheet
 * (which uses react-native-gesture-handler), the gestures conflict on Android.
 *
 * The solution: Wrap the TabBar in a ScrollView from react-native-gesture-handler. This creates a gesture
 * handler boundary that allows the inner FlatList to scroll horizontally while the outer BottomSheet scrolls
 * vertically.
 *
 * On iOS, this wrapper does nothing as iOS handles nested scrolling properly by default.
 */
export const NestedScrollableTabBar = ({ children }: NestedScrollableTabBarProps) => {
    if (Platform.OS !== 'android') {
        // iOS handles nested scrolling properly, no wrapper needed
        return <>{children}</>;
    }

    // Use ScrollView from react-native-gesture-handler
    // This doesn't actually scroll (content fits), but it registers with the gesture system
    // so the inner FlatList can receive horizontal scroll events
    return (
        <ScrollView
            horizontal
            nestedScrollEnabled={true}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.contentContainer}
            style={styles.container}
        >
            {children}
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    container: {
        flexGrow: 0,
    },
    contentContainer: {
        flexGrow: 1,
    },
});
